import React, { useEffect, useMemo, useState } from "react";
import activityLogService from '../../../services/activityLogService';
import Pagination from "react-js-pagination";
import moment from 'moment';
import { Input, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import Select from '../../../components/select';
import DatePicker from '../../../components/datepicker';

const maxLimit = 16

type UserOption = { _id: string; name?: string; email?: string };

export const ProductDashboard = () => {
  const [logs, setlogs] = useState<any>([]);
  const [logsTotal, setlogsTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [currentAlertLogPage, setCurrentAlertLogPage] = useState(1)

  // component states
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [userFilter, setUserFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Trends
  const [deviceFilter, setDeviceFilter] = useState<string>(''); // '' | 'mobile' | 'desktop'
  const [trendPoints, setTrendPoints] = useState<{ date: string; count: number }[]>([]);

  // Active Users
  const [activeUsers, setActiveUsers] = useState<number>(0);
  const [activeDesktop, setActiveDesktop] = useState<number>(0);
  const [activeMobile, setActiveMobile] = useState<number>(0);
  const [activePeriod, setActivePeriod] = useState<string>('last_7_days');

  const getAllUsers = async () => {
      try {
        const data = await activityLogService.getAllUsers()
  
        // console.log("ALL Users: ", data);
        setAllUsers(data.result);
        return data;
      } catch (err) {
        console.error("Error in getAllUsers:", err);
      }
    };

  const getAllLogs = async (user_id : string, pageNumber: number = 1) => {
      try {
        setLoading(true)

        const { start, end } = computeRangeFromInputs();
        const data = await activityLogService.getAllLogs(user_id, pageNumber, maxLimit, {
          search: search || undefined,
          startDate: start || undefined,
          endDate: end || undefined,
        })
        console.log("ALL logs: ", data);
        if (data.total) {
          setlogsTotal(data.total)
        }
        setlogs(data.logs)
        setlogsTotal(data.total)

        setCurrentAlertLogPage(pageNumber)
        setLoading(false)
        return data;
      } catch (err) {
        console.error("Error in getAllLogs:", err);
      }
    };

  useEffect(() => {
    getAllUsers();
    getAllLogs("");
    
    // initial overview cards
    fetchTrends();
    fetchActiveUsers();
  }, []);

  const handleUserFilterChange = (user: any) => {
      const selectedId = user?._id || '';
      setUserFilter(selectedId);
      getAllLogs(selectedId);
  };

  const computeRangeFromInputs = () => {
    const start = startDate ? moment(startDate).startOf('day').valueOf() : 0;
    const end = endDate ? moment(endDate).endOf('day').valueOf() : 0;
    return { start, end };
  };

  const fetchTrends = async () => {
    const { start, end } = computeRangeFromInputs();
    const data = await activityLogService.getUserTrends({
      startDate: start || undefined,
      endDate: end || undefined,
      device: deviceFilter || undefined,
      action: 'LOGIN_SUCCESS',
    });
    setTrendPoints(data.points || []);
  };

  const computePeriodRange = useMemo(() => {
    const now = moment();
    switch (activePeriod) {
      case 'today':
        return { start: now.clone().startOf('day').valueOf(), end: now.clone().endOf('day').valueOf() };
      case 'last_7_days':
        return { start: now.clone().subtract(6, 'days').startOf('day').valueOf(), end: now.clone().endOf('day').valueOf() };
      case 'last_30_days':
        return { start: now.clone().subtract(29, 'days').startOf('day').valueOf(), end: now.clone().endOf('day').valueOf() };
      case 'this_month':
        return { start: now.clone().startOf('month').valueOf(), end: now.clone().endOf('month').valueOf() };
      case 'last_month':
        return { start: now.clone().subtract(1, 'month').startOf('month').valueOf(), end: now.clone().subtract(1, 'month').endOf('month').valueOf() };
      default:
        return { start: 0, end: 0 };
    }
  }, [activePeriod]);

  const fetchActiveUsers = async () => {
    const { start, end } = computePeriodRange;
    const data = await activityLogService.getActiveUsersCount({
      startDate: start || undefined,
      endDate: end || undefined,
      action: 'LOGIN_SUCCESS',
    });
    setActiveUsers(data.count || 0);
    const desktop = await activityLogService.getActiveUsersCount({ startDate: start || undefined, endDate: end || undefined, action: 'LOGIN_SUCCESS', device: 'desktop' });
    const mobile = await activityLogService.getActiveUsersCount({ startDate: start || undefined, endDate: end || undefined, action: 'LOGIN_SUCCESS', device: 'mobile' });
    setActiveDesktop(desktop.count || 0);
    setActiveMobile(mobile.count || 0);
  };

  useEffect(() => {
    fetchActiveUsers();
  }, [activePeriod]);

  useEffect(() => {
    fetchTrends();
  }, [deviceFilter]);

  // dynamic filters: search and date range debounce
  useEffect(() => {
    const t = setTimeout(() => {
      getAllLogs(userFilter);
      fetchTrends();
    }, 400);
    return () => clearTimeout(t);
  }, [search, startDate, endDate]);


  return (
    <div className="container bs-docs-container">
      {/* Headline for Overview */}
      <div className="row mb-25">
        <div className="col-xs-12 col-sm-12 col-md-3 col-lg-3 col-xl-2">
          <p className="table-title media-padding">Overview</p>
        </div>
      </div>

      {/* Filters row - full width container with 4 equal columns */}
      <div className="row mb-3">
        <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <div className="filter-select">
            <label className="custom-label">User</label>
            <Select
              name="userFilter"
              data={[{ _id: '', name: 'All' }, ...allUsers]}
              value={allUsers.find((u) => u._id === userFilter) || { _id: '', name: 'All' }}
              onChange={handleUserFilterChange}
            />
          </div>
          <div className="filter-select">
            <label className="custom-label">From</label>
            <DatePicker
              value={startDate}
              onChange={(d: any) => setStartDate(d)}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              isClearable
              placeholderText="From"
            />
          </div>
          <div className="filter-select">
            <label className="custom-label">To</label>
            <DatePicker
              value={endDate}
              onChange={(d: any) => setEndDate(d)}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              isClearable
              placeholderText="To"
            />
          </div>
          <div className="filter-select">
            <label className="custom-label">Search</label>
            <Input.Search
              className="search-form custom-form-control"
              placeholder="Search by name or action"
              // style={{padding:"9px 11px"}}
              size="large"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              enterButton
            />
          </div>
        </div>
      </div>

      {/* Cards row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginTop: 8 }}>
        <div className="custom-card online-device border-0" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ margin: 0 }}>Active Users</h5>
            <select value={activePeriod} onChange={(e) => setActivePeriod(e.target.value)}>
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
            </select>
          </div>
          <div style={{ fontSize: 42, fontWeight: 600, marginTop: 16}}>{activeUsers}</div>
          <div className="d-flex" style={{ marginTop: 28, color: '#4F8C92' }}>
            <div className="flex-fill">Desktop: {activeDesktop}</div>
            <div className="flex-auto">Mobile: {activeMobile}</div>
          </div>
        </div>

        <div className="custom-card online-device border-0" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ margin: 0 }}>User Trends</h5>
            <div>
              <label style={{ marginRight: 8 }}>Device</label>
              <select value={deviceFilter} onChange={(e) => { setDeviceFilter(e.target.value); }}>
                <option value="">All</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
              </select>
              <Button
                className="ml-3 product-dashboard-refresh-btn"
                onClick={fetchTrends}
                title="Refresh"
                shape="round"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#4F8C92',
                  borderColor: '#4F8C92',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                icon={<ReloadOutlined style={{ color: '#fff', fontSize: 18 }} />}
              />
            </div>
          </div>
          <div style={{ height: 260, marginTop:12 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendPoints.map(p => ({ x: p.date, y: p.count }))} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis name="Date" dataKey="x" tickFormatter={(d) => moment(d).format('MM/DD')} />
                <YAxis name="Active Users" allowDecimals={false} />
                <Tooltip labelFormatter={(d) => moment(d as any).format('MMM DD, YYYY')} />
                <Line type="monotone" dataKey="y" stroke="#4F8C92" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="row mb-2 mt-3">
        <div className="col-xs-12 col-sm-12 col-md-3 col-lg-3 col-xl-2">
          <p className="table-title media-padding">Activity Logs</p>
        </div>
      </div>
      <div className="alert-table">
        <table className="table table-custom custom-table table-style table-hover responsive-table" style={{ width: "100%", display: "block", overflowX: "auto", whiteSpace: "nowrap" }}>
          <thead className="table-header-bg" style={{ backgroundColor: '#4F8C92', color: 'white' }}>
            <tr>
              <th>User</th>
              <th>Action</th>
              <th>Description</th>
              <th>Category</th>
              <th>Metadata</th>
              <th>Date Time</th>
            </tr>
          </thead>
          <tbody>
            {logs?.length > 0 ? (
              logs?.map((log: any, index: any) => (
                <tr
                  key={log?._id}
                >
                  <td>{log?.user?.name || log?.user?.email}</td>
                  <td>{log?.action || "-"}</td>
                  <td>{log?.description ? log.description : "-"}</td>
                  <td>{log?.category ? log.category : "-"}</td>
                  <td>
                    {log?.metadata && typeof log.metadata === 'object'
                      ? Object.values(log.metadata)
                      .filter(value => value !== null && value !== undefined)
                      .join(', ')
                      : '-'}
                  </td>
                  <td>{log?.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No activity found!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {logs.length > 0 && !loading &&
            <div className={Math.ceil(logsTotal / maxLimit) > 1 ? "pagination-container-logs" : "pagination-container-no-logs"}>
              {Math.ceil(logsTotal / maxLimit) > 1 ?
                <Pagination
                  hideNavigation={true}
                  activePage={currentAlertLogPage}
                  itemsCountPerPage={maxLimit}
                  totalItemsCount={logsTotal}
                  itemClass={"item-class"}
                  activeClass={"active-class"}
                  pageRangeDisplayed={5}
                  onChange={(pageNumber) => getAllLogs(userFilter, pageNumber)}
                />
                : null
              }
            </div>
          }
    </div>
  );
};